import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { Category, Product } from "@/lib/types";
import type { CategoryLayout } from "@/lib/restaurantSettings";
import { getTranslatedText } from "@/lib/translations";
import { CategorySidebar } from "./CategorySidebar";
import { ProductCard } from "./ProductCard";

interface Props {
  productsById: Map<string, Product>;
  categoriesById: Map<string, Category>;
  isFrozen: boolean;
  /** Mode consultation : pas d'ajout rapide. */
  readOnly?: boolean;
  /** Quantité au panier par produit (badge sur la carte). */
  cartQtyByProduct?: Record<string, number>;
  /** Un ajout de plus est-il possible ? (désactive le « + »). */
  canQuickAdd?: (product: Product) => boolean;
  onOpen: (product: Product, sourceEl: Element | null) => void;
  onQuickAdd?: (product: Product, sourceEl: Element | null) => void;
  /** Page unique : un titre de section ouvre le sélecteur. Catégories à gauche : colonne de navigation. */
  onOpenCategoryPicker: () => void;
  /** Affichage choisi dans l'admin (getCategoryLayout) ; absent = page unique. */
  layout?: CategoryLayout;
}

const ChevronDown = (): JSX.Element => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M3 5 L7 9 L11 5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const MenuList = ({
  productsById,
  categoriesById,
  isFrozen,
  readOnly = false,
  cartQtyByProduct,
  canQuickAdd,
  onOpen,
  onQuickAdd,
  onOpenCategoryPicker,
  layout = "onePage",
}: Props): JSX.Element => {
  const { t, i18n } = useTranslation();
  const grouped = useMemo(() => {
    const map = new Map<string, Product[]>();
    for (const product of productsById.values()) {
      const catId = product.categoryId || "_uncategorized";
      const arr = map.get(catId) || [];
      arr.push(product);
      map.set(catId, arr);
    }
    // Ordre défini par l'admin (tablette : `.sort((a, b) => a.index < b.index ? -1 : 1)`).
    for (const arr of map.values()) arr.sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
    return map;
  }, [productsById]);

  const sortedCategories = useMemo(() => {
    return Array.from(categoriesById.values()).sort(
      (a, b) => (a.index ?? 0) - (b.index ?? 0)
    );
  }, [categoriesById]);

  // Seules les catégories qui ont des produits : sections et colonne de navigation identiques.
  const visibleCategories = useMemo(
    () => sortedCategories.filter((category) => grouped.get(category.id)?.length),
    [sortedCategories, grouped]
  );

  const sidebar = layout === "sidebar";

  const sections = visibleCategories.map((category) => {
    const items = grouped.get(category.id) ?? [];
    const name = getTranslatedText(category, "name", i18n.language);
    return (
      <section
        key={category.id}
        id={`section-${category.id}`}
        aria-labelledby={`cat-${category.id}`}
        style={{ scrollMarginTop: "var(--header-h, 88px)" }}
      >
        <header
          className={`sticky z-10 bg-bg/95 backdrop-blur-sm pt-3 pb-3 mb-4 flex items-center gap-3 border-b border-border/60 ${
            sidebar ? "-ml-2.5 -mr-3 pl-2.5 pr-3" : "-mx-5 px-5"
          }`}
          style={{ top: "var(--header-h, 88px)" }}
        >
          {sidebar ? (
            <h2
              id={`cat-${category.id}`}
              className="display-font text-xl font-semibold text-ink leading-tight min-w-0 break-words"
            >
              {name}
            </h2>
          ) : (
            <button
              type="button"
              onClick={onOpenCategoryPicker}
              className="display-font text-2xl font-semibold text-ink flex items-center gap-2 hover:text-primary transition-colors"
              aria-label="Choisir une catégorie"
            >
              <span id={`cat-${category.id}`}>{name}</span>
              <span className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center transition-transform">
                <ChevronDown />
              </span>
            </button>
          )}
          <span className="flex-1 h-px bg-border" aria-hidden="true" />
          <span className="text-xs text-ink-muted tabular-nums">{items.length}</span>
        </header>
        {category.maxQuantity && category.maxQuantity > 0 ? (
          <div className="mb-4 -mt-2 flex items-center justify-center gap-2 text-xs text-ink-soft bg-warm/10 border border-warm/30 rounded-pill px-3 py-1.5">
            <span aria-hidden="true">⚠️</span>
            <span>
              {t("menu.categoryLimit", {
                n: category.maxQuantity,
                defaultValue: `${category.maxQuantity} choix / personne pour tout le service`,
              })}
            </span>
          </div>
        ) : null}
        <div className={`grid grid-cols-2 ${sidebar ? "gap-2" : "gap-3"}`}>
          {items.map((p) => (
            <ProductCard
              key={p.id}
              product={p}
              disabled={isFrozen}
              readOnly={readOnly}
              cartQty={cartQtyByProduct?.[p.id] ?? 0}
              canQuickAdd={canQuickAdd ? canQuickAdd(p) : true}
              onOpen={(sourceEl) => onOpen(p, sourceEl)}
              onQuickAdd={onQuickAdd ? (sourceEl) => onQuickAdd(p, sourceEl) : undefined}
            />
          ))}
        </div>
      </section>
    );
  });

  if (sidebar) return <CategorySidebar categories={visibleCategories}>{sections}</CategorySidebar>;

  return <div className="px-5 pb-32 space-y-10">{sections}</div>;
};
